import { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navLinks } from '@/mocks/homeData';
import { useScrollSpy } from '@/hooks/useScroll';

export default function Navbar() {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomepage = location.pathname === '/';
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sectionIds = navLinks
    .filter((l) => l.href.startsWith('/#'))
    .map((l) => l.href.replace('/#', ''));
  const activeSection = useScrollSpy(sectionIds);

  const visibleLinks = navLinks.filter((link) => link.label !== 'Capabilities' && link.label !== 'Quality');

  useEffect(() => {
    setOpenDropdown(null);
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    setOpenDropdown(null);

    if (href === '#') return;

    if (href.startsWith('/#')) {
      const sectionId = href.replace('/#', '');
      if (isHomepage) {
        const el = document.getElementById(sectionId);
        if (el) {
          const navHeight = 80;
          const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      } else {
        navigate('/', { state: { scrollTo: sectionId } });
      }
    } else {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const isActive = (link: (typeof navLinks)[number]) => {
    if ('children' in link) {
      return location.pathname.startsWith('/sister-concerns');
    }
    if (link.href === '/about') {
      return location.pathname === '/about';
    }
    if (link.href.startsWith('/#')) {
      const id = link.href.replace('/#', '');
      return isHomepage && activeSection === id;
    }
    return false;
  };

  return (
    <nav className="sticky top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-background-200/80">
      <div className="w-full px-4 md:px-6 xl:px-10">
        <div className="flex items-center h-16 md:h-[72px]">

          {/* Logo */}
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              if (isHomepage) {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
            }}
            className="flex items-center gap-3 group flex-shrink-0 mr-4 md:mr-8"
          >
            <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center transition-shadow duration-300 group-hover:shadow-[0_0_20px_rgba(0,0,0,0.12)]">
              <img
                src="/images/logo.jpeg"
                alt="Beaumonde Tex Logo"
                className="w-8 h-8 md:w-10 md:h-10"
                style={{ aspectRatio: '1 / 1' }}
              />
            </div>
            <div className="hidden sm:block">
              <div className="font-heading text-[13px] md:text-sm font-semibold leading-tight whitespace-nowrap text-foreground-800">
               Beaumonde Tex Ltd.
              </div>
            </div>
          </a>

          {/* Nav Links — centered */}
          <div className="flex items-center gap-0.5 md:gap-1 flex-1 justify-center">
            {visibleLinks.map((link) => {
              if ('children' in link) {
                const isDropdownOpen = openDropdown === link.label;
                return (
                  <div
                    key={link.label}
                    className="relative"
                    ref={isDropdownOpen ? dropdownRef : undefined}
                    onMouseEnter={() => setOpenDropdown(link.label)}
                    onMouseLeave={() => setOpenDropdown(null)}
                  >
                    <button
                      onClick={() => setOpenDropdown(isDropdownOpen ? null : link.label)}
                      className={`relative px-2 md:px-3.5 py-2 rounded-md text-[12px] md:text-[13px] font-medium whitespace-nowrap transition-all duration-200 flex items-center gap-0.5 md:gap-1 cursor-pointer ${
                        isActive(link)
                          ? 'text-primary-500'
                          : 'text-foreground-500 hover:text-foreground-800'
                      }`}
                    >
                      {isActive(link) && (
                        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-[2px] bg-primary-500 rounded-full" />
                      )}
                      {link.label}
                      <i className={`ri-arrow-down-s-line text-[10px] md:text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
                    </button>
                    {isDropdownOpen && (
                      <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-[220px] md:w-[260px] bg-white rounded-xl shadow-[0_8px_30px_rgba(0,0,0,0.08)] border border-background-200/60 py-2 z-50 animate-[fadeIn_200ms_ease-out]">
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rotate-45 bg-white border-l border-t border-background-200/60" />
                        {link.children.map((child) => (
                          <a
                            key={child.href}
                            href={child.href}
                            onClick={(e) => {
                              e.preventDefault();
                              setOpenDropdown(null);
                              handleNavClick(child.href);
                            }}
                            className="block px-5 py-2.5 text-[13px] md:text-sm text-foreground-600 hover:text-primary-500 hover:bg-background-50 transition-colors duration-150 whitespace-nowrap"
                          >
                            {child.label}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }

              return (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => {
                    e.preventDefault();
                    handleNavClick(link.href);
                  }}
                  className={`relative px-2 md:px-3.5 py-2 rounded-md text-[12px] md:text-[13px] font-medium whitespace-nowrap transition-all duration-200 ${
                    isActive(link)
                      ? 'text-primary-500'
                      : 'text-foreground-500 hover:text-foreground-800'
                  }`}
                >
                  {isActive(link) && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-5 h-[2px] bg-primary-500 rounded-full" />
                  )}
                  {link.label}
                </a>
              );
            })}
          </div>

          {/* CTA — right side */}
          <div className="flex items-center flex-shrink-0 ml-4 md:ml-8">
            <a
              href="/#contact"
              onClick={(e) => {
                e.preventDefault();
                handleNavClick('/#contact');
              }}
              className="inline-flex items-center px-3.5 py-2 md:px-5 md:py-2.5 bg-primary-500 text-white text-[12px] md:text-[13px] font-semibold font-heading rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap hover:shadow-[0_4px_14px_rgba(0,0,0,0.15)]"
            >
              Send Inquiry
            </a>
          </div>

        </div>
      </div>
    </nav>
  );
}